import { apiFetch } from '@/api/apiFetch';
import { getMobietrainApiUrl } from './config';

interface CachedToken {
  value: string;
  expiresAt: number;
}

let cached: CachedToken | null = null;
let inflight: Promise<string> | null = null;

const DEFAULT_TTL_SECONDS = 600;
const REFRESH_BUFFER_MS = 30_000;

const fetchSsoJwt = async (): Promise<string> => {
  const res = await apiFetch('/academy/token', { method: 'POST' });
  if (!res.ok) {
    throw new Error(`Failed to fetch Mobietrain SSO JWT (${res.status})`);
  }
  const body = (await res.json()) as { token: string };
  if (!body.token) {
    throw new Error('Mobietrain SSO response missing token field');
  }
  return body.token;
};

const exchangeJwtForAccessToken = async (ssoJwt: string): Promise<string> => {
  const params = new URLSearchParams();
  params.set('token', ssoJwt);
  // `credentials: 'include'` lets the browser store the Partitioned (CHIPS) cookie
  // this endpoint sets. GraphQL/REST calls authenticate via the access_token from
  // the response body (Bearer header), but media <img> requests can't carry a
  // header — they rely on that partitioned cookie. The endpoint's CORS allows
  // credentials for the Khova origins.
  const endpoint = `${getMobietrainApiUrl()}/authentication/ott-exchange`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to exchange Mobietrain SSO JWT (${res.status})`);
  }
  const body = (await res.json()) as { access_token?: string };
  if (!body.access_token) {
    throw new Error('Mobietrain ott-exchange response missing access_token');
  }
  return body.access_token;
};

const fetchFreshToken = async (): Promise<string> => {
  const ssoJwt = await fetchSsoJwt();
  const accessToken = await exchangeJwtForAccessToken(ssoJwt);
  cached = {
    value: accessToken,
    expiresAt: Date.now() + DEFAULT_TTL_SECONDS * 1000,
  };
  return accessToken;
};

export const fetchMobietrainToken = async (): Promise<string> => {
  if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
    return cached.value;
  }
  if (!inflight) {
    inflight = fetchFreshToken().finally(() => {
      inflight = null;
    });
  }
  return inflight;
};

export const clearMobietrainToken = (): void => {
  cached = null;
};
